import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  ArrowLeft, Settings, DollarSign, Loader2, Save, Check, Plus, Pencil, Trash2, 
  GripVertical, ToggleLeft, ToggleRight, Package, Star, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { StripeStatusCard } from '@/components/admin/StripeStatusCard';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSettings() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [settings, setSettings] = useState(null);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  
  // Settings form state
  const [taxRatePercent, setTaxRatePercent] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState('');
  const [googleReviewLink, setGoogleReviewLink] = useState('');
  const [reviewEmailsEnabled, setReviewEmailsEnabled] = useState(false);
  
  // Add-on dialog state
  const [addonDialogOpen, setAddonDialogOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [addonForm, setAddonForm] = useState({
    key: '',
    name: '',
    description: '',
    price_cents: '',
    per_unit: false,
    unit_label: '',
    enabled: true,
  });
  const [addonSaving, setAddonSaving] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAddon, setDeletingAddon] = useState(null);

  const isAdmin = user?.roles?.includes('admin');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/settings`);
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      setSettings(data);
      setTaxRatePercent((data.tax_rate * 100).toFixed(2));
      setPaymentTermsDays(String(data.payment_terms_days));
      setGoogleReviewLink(data.google_review_link || '');
      setReviewEmailsEnabled(data.review_emails_enabled || false);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load settings');
    }
  }, []);

  const fetchAddons = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/settings/addons`);
      if (!response.ok) throw new Error('Failed to fetch add-ons');
      const data = await response.json();
      setAddons(data);
    } catch (err) {
      console.error('Failed to fetch add-ons:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      setLoading(true);
      Promise.all([fetchSettings(), fetchAddons()]).finally(() => setLoading(false));
    }
  }, [isAuthenticated, isAdmin, fetchSettings, fetchAddons]);

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        tax_rate: parseFloat(taxRatePercent) / 100,
        payment_terms_days: parseInt(paymentTermsDays, 10),
        google_review_link: googleReviewLink.trim(),
        review_emails_enabled: reviewEmailsEnabled,
      };
      
      if (isNaN(payload.tax_rate) || payload.tax_rate < 0 || payload.tax_rate > 1) {
        throw new Error('Tax rate must be between 0% and 100%');
      }
      if (isNaN(payload.payment_terms_days) || payload.payment_terms_days < 1 || payload.payment_terms_days > 90) {
        throw new Error('Payment terms must be between 1 and 90 days');
      }
      
      const response = await authFetch(`${API_BASE}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save settings');
      }
      
      const data = await response.json();
      setSettings(data);
      setSaved(true);
      toast.success('Settings saved');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Add-on CRUD handlers
  const openAddonDialog = (addon = null) => {
    if (addon) {
      setEditingAddon(addon);
      setAddonForm({
        key: addon.key,
        name: addon.name,
        description: addon.description || '',
        price_cents: (addon.price_cents / 100).toFixed(2),
        per_unit: addon.per_unit || false,
        unit_label: addon.unit_label || '',
        enabled: addon.enabled,
      });
    } else {
      setEditingAddon(null);
      setAddonForm({
        key: '',
        name: '',
        description: '',
        price_cents: '',
        per_unit: false,
        unit_label: '',
        enabled: true,
      });
    }
    setAddonDialogOpen(true);
  };

  const handleSaveAddon = async () => {
    setAddonSaving(true);
    try {
      const payload = {
        key: addonForm.key.toLowerCase().replace(/\s+/g, '_'),
        name: addonForm.name,
        description: addonForm.description || null,
        price_cents: Math.round(parseFloat(addonForm.price_cents) * 100),
        per_unit: addonForm.per_unit,
        unit_label: addonForm.per_unit ? addonForm.unit_label : null,
        enabled: addonForm.enabled,
      };
      
      if (!payload.name) throw new Error('Name is required');
      if (isNaN(payload.price_cents) || payload.price_cents < 0) throw new Error('Invalid price');
      
      let response;
      if (editingAddon) {
        response = await authFetch(`${API_BASE}/api/admin/settings/addons/${editingAddon.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        if (!payload.key) throw new Error('Key is required');
        response = await authFetch(`${API_BASE}/api/admin/settings/addons`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save add-on');
      }
      
      toast.success(editingAddon ? 'Add-on updated' : 'Add-on created');
      setAddonDialogOpen(false);
      fetchAddons();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddonSaving(false);
    }
  };

  const handleToggleAddon = async (addon) => {
    try {
      const response = await authFetch(`${API_BASE}/api/admin/settings/addons/${addon.id}/toggle`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to toggle add-on');
      toast.success(`${addon.name} ${addon.enabled ? 'disabled' : 'enabled'}`);
      fetchAddons();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteAddon = async () => {
    if (!deletingAddon) return;
    try {
      const response = await authFetch(`${API_BASE}/api/admin/settings/addons/${deletingAddon.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete add-on');
      toast.success('Add-on deleted');
      setDeleteDialogOpen(false);
      setDeletingAddon(null);
      fetchAddons();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const confirmDelete = (addon) => {
    setDeletingAddon(addon);
    setDeleteDialogOpen(true);
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  // Access denied
  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl text-white mb-4">Access Denied</h1>
        <Button asChild><Link to="/">Return Home</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button
            asChild
            variant="ghost"
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Admin Hub
            </Link>
          </Button>

          <div className="flex items-center gap-3">
            <Settings className="h-7 w-7 text-[#C6A14A]" />
            <h1 className="text-2xl font-light text-white tracking-wide">Admin Settings</h1>
          </div>
          <p className="text-[#E8E4DE]/60 mt-2">Configure invoice defaults, tax rate, and add-ons</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <StripeStatusCard />

        {/* Tax & Payment Terms */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#C6A14A]" />
              Invoice Settings
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Configure tax rate and payment terms
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/80">Tax Rate (%)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={taxRatePercent}
                    onChange={(e) => setTaxRatePercent(e.target.value)}
                    placeholder="0.00"
                    className="bg-[#050816]/50 border-white/10 text-white pr-10"
                    data-testid="tax-rate-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#E8E4DE]/40">%</span>
                </div>
                <p className="text-xs text-[#E8E4DE]/50">Example: 8.6% = enter 8.60</p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/80">Payment Terms (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="90"
                  value={paymentTermsDays}
                  onChange={(e) => setPaymentTermsDays(e.target.value)}
                  placeholder="14"
                  className="bg-[#050816]/50 border-white/10 text-white"
                  data-testid="payment-terms-input"
                />
                <p className="text-xs text-[#E8E4DE]/50">Net payment due in N days</p>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                data-testid="save-settings-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <><Check className="h-4 w-4 mr-2" />Saved</> : <><Save className="h-4 w-4 mr-2" />Save Settings</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Google Reviews Settings */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Star className="h-5 w-5 text-[#C6A14A]" />
              Google Reviews
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Configure automated review request emails sent 2 days after payment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[#E8E4DE]/80">Google Review Link</Label>
              <div className="relative">
                <Input
                  type="url"
                  value={googleReviewLink}
                  onChange={(e) => setGoogleReviewLink(e.target.value)}
                  placeholder="https://g.page/r/your-business/review"
                  className="bg-[#050816]/50 border-white/10 text-white pr-10"
                  data-testid="google-review-link-input"
                />
                {googleReviewLink && (
                  <a
                    href={googleReviewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#C6A14A] hover:text-[#D4AF5A]"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
              <p className="text-xs text-[#E8E4DE]/50">
                Get this link from your Google Business Profile &gt; Ask for Reviews
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-[#050816]/50 rounded-lg border border-white/5">
              <div className="flex items-center gap-3">
                <Switch
                  checked={reviewEmailsEnabled}
                  onCheckedChange={setReviewEmailsEnabled}
                  data-testid="review-emails-toggle"
                />
                <div>
                  <Label className="text-[#E8E4DE]/80">Enable Automated Review Emails</Label>
                  <p className="text-xs text-[#E8E4DE]/50">
                    Sends a friendly review request 2 days after payment
                  </p>
                </div>
              </div>
              {reviewEmailsEnabled && !googleReviewLink && (
                <span className="text-xs text-yellow-400">Set review link above</span>
              )}
            </div>
            
            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <><Check className="h-4 w-4 mr-2" />Saved</> : <><Save className="h-4 w-4 mr-2" />Save Settings</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Add-ons Management */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#C6A14A]" />
                  Invoice Add-ons
                </CardTitle>
                <CardDescription className="text-[#E8E4DE]/60">
                  Manage add-on services for real estate photography
                </CardDescription>
              </div>
              <Button
                onClick={() => openAddonDialog()}
                className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                data-testid="add-addon-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {addons.length === 0 ? (
              <p className="text-[#E8E4DE]/50 text-center py-8">No add-ons configured yet</p>
            ) : (
              <div className="space-y-3">
                {addons.map((addon) => (
                  <div
                    key={addon.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border ${
                      addon.enabled 
                        ? 'bg-[#050816]/50 border-white/10' 
                        : 'bg-[#050816]/30 border-white/5 opacity-60'
                    }`}
                    data-testid={`addon-row-${addon.key}`}
                  >
                    <GripVertical className="h-5 w-5 text-[#E8E4DE]/30 cursor-grab" />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-medium truncate">{addon.name}</h4>
                        {addon.per_unit && (
                          <span className="text-xs bg-[#C6A14A]/20 text-[#C6A14A] px-2 py-0.5 rounded">
                            per {addon.unit_label || 'unit'}
                          </span>
                        )}
                        {addon.price_cents === 2500 && addon.key === 'ai_editing' && (
                          <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">
                            PLACEHOLDER PRICE
                          </span>
                        )}
                      </div>
                      {addon.description && (
                        <p className="text-[#E8E4DE]/50 text-sm truncate">{addon.description}</p>
                      )}
                    </div>
                    
                    <div className="text-right">
                      <p className="text-[#C6A14A] font-semibold">
                        ${(addon.price_cents / 100).toFixed(2)}
                        {addon.per_unit && <span className="text-xs text-[#E8E4DE]/50">/{addon.unit_label || 'unit'}</span>}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleAddon(addon)}
                        className={addon.enabled ? 'text-green-500 hover:text-green-400' : 'text-[#E8E4DE]/40 hover:text-[#E8E4DE]/60'}
                        title={addon.enabled ? 'Disable' : 'Enable'}
                      >
                        {addon.enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openAddonDialog(addon)}
                        className="text-[#E8E4DE]/60 hover:text-white"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => confirmDelete(addon)}
                        className="text-red-400/60 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Last Updated */}
        {settings?.updated_at && (
          <p className="text-center text-[#E8E4DE]/40 text-sm">
            Last updated: {new Date(settings.updated_at).toLocaleString()}
            {settings.updated_by && ` by ${settings.updated_by}`}
          </p>
        )}
      </div>

      {/* Add/Edit Add-on Dialog */}
      <Dialog open={addonDialogOpen} onOpenChange={setAddonDialogOpen}>
        <DialogContent className="bg-[#0f1724] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{editingAddon ? 'Edit Add-on' : 'New Add-on'}</DialogTitle>
            <DialogDescription className="text-[#E8E4DE]/60">
              {editingAddon ? 'Update add-on details' : 'Create a new invoice add-on'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingAddon && (
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/80">Key (unique identifier)</Label>
                <Input
                  value={addonForm.key}
                  onChange={(e) => setAddonForm({ ...addonForm, key: e.target.value })}
                  placeholder="e.g., virtual_staging"
                  className="bg-[#050816]/50 border-white/10 text-white"
                />
                <p className="text-xs text-[#E8E4DE]/50">Lowercase, no spaces (auto-converted)</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-[#E8E4DE]/80">Name</Label>
              <Input
                value={addonForm.name}
                onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                placeholder="e.g., Virtual Staging"
                className="bg-[#050816]/50 border-white/10 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#E8E4DE]/80">Description (optional)</Label>
              <Input
                value={addonForm.description}
                onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                placeholder="Brief description of the add-on"
                className="bg-[#050816]/50 border-white/10 text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/80">Price ($)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#E8E4DE]/40" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addonForm.price_cents}
                    onChange={(e) => setAddonForm({ ...addonForm, price_cents: e.target.value })}
                    placeholder="0.00"
                    className="pl-9 bg-[#050816]/50 border-white/10 text-white"
                  />
                </div>
              </div>
              
              {addonForm.per_unit && (
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80">Unit Label</Label>
                  <Input
                    value={addonForm.unit_label}
                    onChange={(e) => setAddonForm({ ...addonForm, unit_label: e.target.value })}
                    placeholder="e.g., photo, room, hour"
                    className="bg-[#050816]/50 border-white/10 text-white"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-3">
                <Switch
                  checked={addonForm.per_unit}
                  onCheckedChange={(checked) => setAddonForm({ ...addonForm, per_unit: checked })}
                />
                <Label className="text-[#E8E4DE]/80">Price per unit (allows quantity)</Label>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  checked={addonForm.enabled}
                  onCheckedChange={(checked) => setAddonForm({ ...addonForm, enabled: checked })}
                />
                <Label className="text-[#E8E4DE]/80">Enabled (visible in invoice form)</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddonDialogOpen(false)} className="border-white/10 text-[#E8E4DE]">
              Cancel
            </Button>
            <Button onClick={handleSaveAddon} disabled={addonSaving} className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]">
              {addonSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingAddon ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#0f1724] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Add-on</DialogTitle>
            <DialogDescription className="text-[#E8E4DE]/60">
              Are you sure you want to delete "{deletingAddon?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-white/10 text-[#E8E4DE]">
              Cancel
            </Button>
            <Button onClick={handleDeleteAddon} className="bg-red-600 text-white hover:bg-red-700">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
